import {
  createBoardState,
  createInitialBoard,
  hasAvailableMoves,
  moveBoard,
  recoverStalledBoard,
  type BoardState,
} from '../board';
import { BALANCE } from '../balance';
import { calculateDamage } from '../damage';
import { createRng } from '../rng';
import type { Json, SeatIndex } from '../types';
import type {
  GameInput,
  InputRejection,
  RuleContext,
  RuleEvent,
  RuleOutcome,
  RuleSet,
  RuleSetup,
  StepResult,
  Transition,
} from './contracts';

export const BATTLE_RULE_SET_ID = '2k48-battle';

export type BattleStatus = 'playing' | 'ko';

export interface BattlePlayerState {
  readonly seat: SeatIndex;
  readonly board: BoardState;
  /** State of this seat's independent deterministic spawn stream. */
  readonly rngState: number;
  readonly hp: number;
  readonly score: number;
  readonly combo: number;
  readonly maxCombo: number;
  readonly maxTile: number;
  readonly moves: number;
  readonly damageDealt: number;
  readonly damageTaken: number;
  readonly lastMoveAt: number;
  readonly lastInputSeq: number;
}

export interface BattleState {
  readonly seed: string;
  readonly status: BattleStatus;
  readonly players: readonly BattlePlayerState[];
}

/**
 * The complete authoritative rules for one 2K48 versus match.
 *
 * Each seat owns an independent board and RNG stream. A swipe only produces
 * tile transitions for its board; damage is represented by a one-shot event
 * while the resulting HP remains in state for patches and reconnection.
 */
export const battleRuleSet: RuleSet<BattleState> = {
  id: BATTLE_RULE_SET_ID,
  version: 1,

  createInitialState(setup: RuleSetup): BattleState {
    return {
      seed: setup.seed,
      status: 'playing',
      players: Array.from({ length: setup.seats }, (_, seat) => createPlayer(setup.seed, seat)),
    };
  },

  validate(state: BattleState, input: GameInput, ctx: RuleContext): InputRejection | null {
    if (state.status !== 'playing') {
      return { code: 'out-of-phase', message: 'The battle has already ended.' };
    }
    if (input.kind !== 'swipe' || !isDirection(input.direction)) {
      return { code: 'illegal-move', message: 'Expected a swipe with a valid direction.' };
    }

    const player = state.players.find((candidate) => candidate.seat === input.seat);
    if (!player) return { code: 'not-your-turn', message: 'Seat is not part of this battle.' };
    if (
      !Number.isSafeInteger(input.seq) ||
      input.seq <= player.lastInputSeq ||
      input.seq > 0xffff_ffff
    ) {
      return { code: 'stale-input', message: 'Input sequence is invalid or already processed.' };
    }
    if (player.moves > 0 && ctx.now - player.lastMoveAt < BALANCE.input.minIntervalMs) {
      return { code: 'rate-limited', message: 'Inputs arrived faster than the move budget.' };
    }
    return null;
  },

  reduce(state: BattleState, input: GameInput, ctx: RuleContext): StepResult<BattleState> {
    const actorIndex = state.players.findIndex((candidate) => candidate.seat === input.seat);
    const actor = state.players[actorIndex];
    if (!actor || !isDirection(input.direction)) {
      return rejected(state, 'illegal-move', 'Seat or direction is invalid.');
    }

    const random = createRng(`${state.seed}:seat:${actor.seat}`);
    random.restore(actor.rngState);
    const move = moveBoard(actor.board, input.direction, random);
    if (!move.moved) {
      return rejected(state, 'illegal-move', 'The board does not change in that direction.');
    }

    const damage = calculateDamage(
      move.merges.map((merge) => merge.value),
      actor.combo,
    );
    let board = move.state;
    let transitions: Transition[] = [...move.transitions];
    let stalledPenalty = 0;
    const events: RuleEvent[] = [];

    if (!hasAvailableMoves(board)) {
      const recovery = recoverStalledBoard(board);
      board = recovery.state;
      stalledPenalty = recovery.hpPenalty;
      transitions = [
        ...transitions,
        ...recovery.transitions.map((transition) => ({ ...transition, stage: 2 })),
      ];
      events.push({
        type: 'board:recovered',
        seat: actor.seat,
        data: {
          removed: recovery.removed.length,
          hpPenalty: recovery.hpPenalty,
          hpFrom: actor.hp,
          hpTo: clampHp(actor.hp - recovery.hpPenalty),
        },
      });
    }

    const players = [...state.players];
    const actorHp = clampHp(actor.hp - stalledPenalty);
    let nextActor: BattlePlayerState = {
      ...actor,
      board,
      rngState: random.state(),
      hp: actorHp,
      score: actor.score + move.scoreGain,
      combo: damage.combo,
      maxCombo: Math.max(actor.maxCombo, damage.combo),
      maxTile: Math.max(actor.maxTile, highestTile(board)),
      moves: actor.moves + 1,
      damageTaken: actor.damageTaken + (actor.hp - actorHp),
      lastMoveAt: ctx.now,
      lastInputSeq: input.seq,
    };
    players[actorIndex] = nextActor;

    const targetIndex = players.findIndex((candidate) => candidate.seat !== actor.seat);
    const target = players[targetIndex];
    if (target && damage.damage > 0) {
      const targetHp = clampHp(target.hp - damage.damage);
      const appliedDamage = target.hp - targetHp;
      nextActor = { ...nextActor, damageDealt: actor.damageDealt + appliedDamage };
      players[actorIndex] = nextActor;
      players[targetIndex] = {
        ...target,
        hp: targetHp,
        damageTaken: target.damageTaken + appliedDamage,
      };

      const source = [...move.merges].sort((a, b) => b.value - a.value)[0];
      const powerValue = damage.powerTiles.reduce((highest, value) => Math.max(highest, value), 0);
      events.push({
        type: 'attack:launched',
        seat: actor.seat,
        data: {
          targetSeat: target.seat,
          damage: appliedDamage,
          hpFrom: target.hp,
          hpTo: targetHp,
          combo: damage.combo,
          mergeCount: damage.mergeCount,
          sourceTileId: source?.survivorId ?? '',
          powerValue,
        },
      });
      if (damage.powerActivated) {
        events.push({
          type: 'combo:overload',
          seat: actor.seat,
          data: { powerValue, combo: damage.combo },
        });
      }
    }

    const status: BattleStatus = players.some((player) => player.hp === 0) ? 'ko' : 'playing';
    if (status === 'ko') {
      const knockedOut = players.filter((player) => player.hp === 0).map((player) => player.seat);
      events.push({ type: 'battle:ko', seat: actor.seat, data: { knockedOut } });
    }

    const next: BattleState = { ...state, status, players };
    return { state: next, transitions, events, terminal: status === 'ko' };
  },

  isTerminal(state: BattleState): boolean {
    return state.status === 'ko';
  },

  summarize(state: BattleState): RuleOutcome {
    const winners = winnerSeats(state.players);
    return {
      scores: state.players.map((player) => player.score),
      winners,
      stats: state.players.map((player) => ({
        hp: player.hp,
        moves: player.moves,
        maxTile: player.maxTile,
        maxCombo: player.maxCombo,
        damageDealt: player.damageDealt,
        damageTaken: player.damageTaken,
      })),
    };
  },

  serialize(state: BattleState): Json {
    return state as unknown as Json;
  },

  deserialize(raw: Json): BattleState {
    const state = raw as unknown as BattleState;
    return {
      seed: state.seed,
      status: state.status,
      players: state.players.map((player) => ({
        ...player,
        board: createBoardState(player.board.tiles, player.board.size, player.board.nextTileId),
      })),
    };
  },
};

function createPlayer(seed: string, seat: number): BattlePlayerState {
  const random = createRng(`${seed}:seat:${seat}`);
  const board = createInitialBoard(random);
  return {
    seat,
    board,
    rngState: random.state(),
    hp: BALANCE.versus.startingHp,
    score: 0,
    combo: 0,
    maxCombo: 0,
    maxTile: highestTile(board),
    moves: 0,
    damageDealt: 0,
    damageTaken: 0,
    lastMoveAt: 0,
    lastInputSeq: 0,
  };
}

function rejected(
  state: BattleState,
  code: InputRejection['code'],
  message: string,
): StepResult<BattleState> {
  return { state, transitions: [], events: [], terminal: false, rejected: { code, message } };
}

function winnerSeats(players: readonly BattlePlayerState[]): SeatIndex[] {
  if (players.length === 0) return [];
  const bestHp = Math.max(...players.map((player) => player.hp));
  const byHp = players.filter((player) => player.hp === bestHp);
  const bestScore = Math.max(...byHp.map((player) => player.score));
  const finalists = byHp.filter((player) => player.score === bestScore);
  return finalists.length === 1 ? [finalists[0]!.seat] : [];
}

function isDirection(value: GameInput['direction']): value is NonNullable<GameInput['direction']> {
  return value === 'up' || value === 'down' || value === 'left' || value === 'right';
}

function highestTile(board: BoardState): number {
  return board.tiles.reduce((highest, tile) => Math.max(highest, tile.value), 0);
}

function clampHp(hp: number): number {
  return Math.min(BALANCE.versus.startingHp, Math.max(0, Math.round(hp)));
}
